import { useUserProfileViewCreator } from '@viewCreators/UserProfileViewCreator';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { UserStore } from '@stores/UserStore';
import { useQueryClient } from '@tanstack/react-query';
import { UserProfileViewCreator } from '@viewCreators/UserProfileViewCreator';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '@app/App.hook';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export type UserProfileView = {
  id: number;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  email: string;
  phone: string;
  phoneVerifiedOn: Date;
};

export const useUserProfileController = () => {
  const cookies = useAppCookies();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { logout } = useApp();

  const [saving, setSaving] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [fileError, setFileError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [showTransition, setShowTransition] = useState(() => Boolean(searchParams.get('success')));

  const fileInputRef = useRef<HTMLInputElement>(null);

  const showEditPhone = searchParams.get('showModal') === 'edit';

  const userId = cookies.profile?.id ? Number(cookies.profile.id) : 0;
  const { data: user } = useUserProfileViewCreator(userId);

  // Success transition for phone number update
  if (showTransition) {
    setTimeout(() => setShowTransition(false), 3000);
  }

  // Derived display values — use query data directly, fall back to cookie
  const avatarSrc = user?.avatarUrl || cookies.profile?.picture || '';
  const displayName = user?.name || cookies.profile?.name || '';
  const email = user?.email || cookies.profile?.email || '';
  const phone = user?.phone || '';

  const updateProfile = useCallback(
    async (updates: { name?: string; avatar?: File }) => {
      setSaving(true);
      setSaveError('');
      try {
        const store = new UserStore();
        const response = await store.updateMyProfile(updates);
        if (response.status !== 'SUCCESS' || response.statusCode >= 400) {
          const msg =
            (response.result as any)?.message || response.statusMessage || 'Something went wrong. Please try again.';
          setSaveError(msg);
          return;
        }
        await queryClient.invalidateQueries({ queryKey: [UserProfileViewCreator.name] });
      } catch {
        setSaveError('Something went wrong. Please try again.');
      } finally {
        setSaving(false);
      }
    },
    [queryClient]
  );

  const handleAvatarClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setFileError('');
      setSaveError('');
      const file = e.target.files?.[0];
      if (!file) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        setFileError('Please select a JPEG, PNG, or WebP image.');
        return;
      }
      if (file.size > MAX_SIZE) {
        setFileError('Image must be under 5MB.');
        return;
      }

      await updateProfile({ avatar: file });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [updateProfile]
  );

  const handleEditNameOpen = useCallback(() => {
    setEditName(user?.name || cookies.profile?.name || '');
    setEditNameOpen(true);
  }, [user?.name, cookies.profile?.name]);

  const handleEditNameClose = useCallback(() => {
    setEditNameOpen(false);
  }, []);

  const handleEditNameChange = useCallback((value: string) => {
    setEditName(value);
  }, []);

  const handleSaveName = useCallback(async () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    await updateProfile({ name: trimmed });
    setEditNameOpen(false);
  }, [editName, updateProfile]);

  return {
    view: user,
    saving,
    avatarSrc,
    displayName,
    email,
    phone,
    fileInputRef,
    fileError,
    saveError,
    editNameOpen,
    editName,
    showEditPhone,
    showTransition,
    logout,
    handleAvatarClick,
    handleFileChange,
    handleEditNameOpen,
    handleEditNameClose,
    handleEditNameChange,
    handleSaveName,
  };
};
